export type ChatSourceType = 'WORKFLOW'

export enum ChatDisplayType {
  FILL = 'FILL',
  FLOATING = 'FLOATING',
  FULLSCREEN = 'FULLSCREEN',
}

export enum ChatMessageType {
  TEXT = 'TEXT',
  AUDIO = 'AUDIO',
  ATTACHMENTS = 'ATTACHMENTS',
  SUGGESTED_PROMPT = 'SUGGESTED_PROMPT',
  AI_TRIGGER = 'AI_TRIGGER',
}

export type ChatDisplayTypes =
  | [ChatDisplayType.FILL]
  | [ChatDisplayType.FLOATING]
  | [ChatDisplayType.FULLSCREEN]
  | [ChatDisplayType.FLOATING, ChatDisplayType.FULLSCREEN]

export type ChatFeedbackValue = 'POSITIVE' | 'NEGATIVE'

export interface ChatMessageFeedback {
  value: ChatFeedbackValue
  reason?: string | null
  comment?: string | null
  submittedAt?: string | null
  submittedBy?: string | null
}

export interface ChatApiMessage {
  actor: 'agent' | 'human'
  message: string
  timestamp: string
  state: string
  messageId: string
  feedback?: ChatMessageFeedback | null
  type: ChatMessageType
  audioFile?: { downloadPath: string; duration: number; mimeType: string }
  files?: { downloadPath: string; mimeType: string; originalFileName?: string }[]
  // ATTACHMENTS messages from chat history carry their files under `documents`
  // (the send/SSE flow uses `files`); both are normalized to the store's `files`.
  documents?: {
    fileName: string
    mimeType: string
    size?: number
    downloadPath: string
    filePath?: string
    processingState?: string
    kbStoreUuid?: string
  }[]
}

export interface ChatHistoryResponse {
  id: string
  state: string
  createdAt: string
  messages: ChatApiMessage[]
  total: number
  summary: string | null
  userEmail: string | null
}

export interface ConversationListItem {
  id: string
  state: string | null
  createdAt: string
  summary: string
  messageCount: number
  snippet?: string
}

export interface ConversationsListResponse {
  conversations: ConversationListItem[]
  total: number
}

export interface AudioFileContent {
  downloadPath: string
  duration: number
  mimeType: string
  filePath: string
}

export interface AttachmentFileContent {
  filePath: string
  downloadPath: string
  mimeType: string
  originalFileName: string
}

export type ConversationContext = {
  buildId?: string
  uiFlowResDefId?: string | null
  currentPage?: string | null
  fields?: Record<string, any> | null
}

export type ChatMessageContent =
  | { type: ChatMessageType.TEXT; message: string }
  | { type: ChatMessageType.SUGGESTED_PROMPT; label: string; prompt: string }
  | { type: ChatMessageType.AUDIO; context: ConversationContext; audioFile: AudioFileContent }
  | {
      type: ChatMessageType.ATTACHMENTS
      message: string
      context: ConversationContext
      files: AttachmentFileContent[]
    }
  | {
      type: ChatMessageType.AI_TRIGGER
      aiTrigger: { triggerUuid: string; params: Record<string, any> }
    }

export type ChatStartRequestBody = {
  type: ChatMessageType
  chatSessionId: string
  content: Record<string, unknown>
}
